'use client';

import { memo } from 'react';
import type { RefObject } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowDown02Icon, Cancel01Icon } from '@terra/ui-icons';
import { Box, HStack } from '@terra/ui-styled-system';
import { Button } from '../../../components/button';
import { IconButton } from '../../../components/icon-button';
import { useUnseenChatMessages } from '../hooks/useUnseenChatMessages';
import type { ProcessedMessage } from '../utils/message-processing';

type ChatNewMessagesIndicatorProps = {
  messages: ProcessedMessage[];
  scrollElementRef: RefObject<HTMLDivElement | null>;
  onJumpToLatest: () => void;
};

export const ChatNewMessagesIndicator = memo(function ChatNewMessagesIndicator({
  messages,
  scrollElementRef,
  onJumpToLatest,
}: ChatNewMessagesIndicatorProps) {
  const t = useTranslations('designSystem.chat.chat');
  const tCommon = useTranslations('designSystem.common');
  const { unseenCount, markAllSeen } = useUnseenChatMessages({
    messages,
    scrollElementRef,
  });

  if (unseenCount === 0) {
    return null;
  }

  return (
    <Box position="relative" h={0} w="full">
      <HStack
        position="absolute"
        bottom={2}
        left="50%"
        transform="translateX(-50%)"
        zIndex={1}
        gap={0}
        bg="bg.default"
        borderWidth="1px"
        borderColor="border.default"
        rounded="full"
        shadow="sm"
        data-testid="chat-new-messages-indicator"
      >
        <Button
          size="xs"
          variant="ghost"
          rounded="full"
          onClick={onJumpToLatest}
        >
          <ArrowDown02Icon size={14} />
          {t('newMessages', { count: unseenCount })}
        </Button>
        <IconButton
          aria-label={tCommon('dismiss')}
          size="xs"
          variant="ghost"
          rounded="full"
          onClick={markAllSeen}
        >
          <Cancel01Icon size={12} />
        </IconButton>
      </HStack>
    </Box>
  );
});
